// The in-memory API's runs, verdicts and decision logs (DC-07; services/api/src/routes/agents/
// runs.ts and verdicts.ts), so the Agents screens (M3-08) are built on the real shapes: the run
// list, the run detail with its verdict (`verdict`, `verdict_evidence_ref`, `output_ref`) and the
// first rows of its decision log, the paged log, and a DE's `de.verified_rate`. Runs are written
// by the agent runtime; the one screen write is the supervisor's Resume (AGT-UI-2). Rules kept from the API: the `agents`
// add-on gates every route (403 `feature_disabled`), viewers are company managers and the
// supervisor of the run's seat (05 `run#viewer`; anyone else gets an empty list or 404), the log
// is in seq order, and a month with fewer than 5 runs with a verdict is `insufficient_data`.
import type { OperationOutput, RouteId } from "@companyos/sdk";
import { type Handler, json, notFound, type Principal, page, problem } from "./fake";
import { type FakeDe, uid } from "./fake-data";

/**
 * The seats a person (not a manager) sees runs of, as the API's `visibleSeats` /
 * `supervisedSeatIds`: the seats they hold and the DE seats below them at any depth. The demo
 * models seats only through its DEs: a person holds the supervisor seats the roster names for
 * them.
 */
export function seatsBelow(des: readonly FakeDe[], me: string | null): Set<string> {
  const seats = new Set<string>();
  if (!me) return seats;
  for (const d of des) if (d.supervisor.membership_id === me) seats.add(d.supervisor.seat_id);
  for (let grew = true; grew; ) {
    grew = false;
    for (const d of des)
      if (seats.has(d.supervisor.seat_id) && !seats.has(d.seat_id)) {
        seats.add(d.seat_id);
        grew = true;
      }
  }
  return seats;
}

/**
 * ACTIVITY: one rule for every twin that lists runs (runs.list/get/cancel and
 * workforce.activity): a person sees a run of a seat in `seatsBelow` — matched against the
 * run's recorded seat, so a DE moved to another seat leaves its old runs with the old seat. A run
 * on a seat no demo DE holds (chat's demo runs) falls back to its DE's supervisor.
 */
export function personSeesRun(
  des: readonly FakeDe[],
  me: string | null,
  r: { seat_id: string | null; membership_id: string | null },
  seats: Set<string> = seatsBelow(des, me),
): boolean {
  if (!me) return false;
  if (r.seat_id !== null && seats.has(r.seat_id)) return true;
  if (r.seat_id !== null && des.some((d) => d.seat_id === r.seat_id)) return false;
  const de = des.find((d) => d.membership_id === r.membership_id);
  return Boolean(de && de.supervisor.membership_id === me);
}

type RunRow = OperationOutput<"runs.list">["data"][number];
type DecisionRow = OperationOutput<"runs.decisions">["data"][number];
type Verdict = NonNullable<RunRow["verdict"]>;

/** AGENT-STABILITY: a run that is not an automatic retry and was never lost (retry.ts). */
export const FIRST_ATTEMPT = {
  attempt: 1,
  retry_of_run_id: null,
  retried_by_run_id: null,
  retry_state: null,
  retry_after: null,
  retry_reason: null,
  // RUN-EFFORT: the twin's runs were admitted before tiers (no tier recorded).
  effort: null,
  effort_source: null,
  model_pinned: false,
  // FAILURE-STOP: nothing failed.
  failure_guard: null,
} as const;

/** Mirrors `RUN_DETAIL_DECISIONS` and `VERIFIED_RATE_MIN_RUNS` of the contracts. */
const DETAIL_DECISIONS = 100;
const MIN_RUNS = 5;

export type RunsDeps = {
  now: () => Date;
  desOf: (companyId: string) => FakeDe[];
  agentsOn: (p: Principal) => boolean;
  /** Runs another twin holds (chat's, M2-06), served by the same routes. */
  extraRuns?: (companyId: string) => RunRow[];
};

/** Seeded verdicts per DE, newest last: mostly verified, one of each refusal. */
const PATTERN: (Verdict | null)[] = [
  "verified",
  "verified",
  "not_verified",
  "verified",
  "verified",
  "inconclusive",
  "verified",
  null,
];

export function createRunsHandlers(deps: RunsDeps): Partial<Record<RouteId, Handler>> {
  return createRunsTwin(deps).handlers;
}

/**
 * The twin's handlers and its seeded runs: the merged fake (fake.ts) lists these runs through the
 * Agents twin (M3-08) and serves their detail and decision log here.
 */
export function createRunsTwin(deps: RunsDeps): {
  handlers: Partial<Record<RouteId, Handler>>;
  runsOf: (companyId: string) => RunRow[];
} {
  const runs = new Map<string, RunRow[]>();
  const logs = new Map<string, DecisionRow[]>();
  const companyNo = (id: string) => Number.parseInt(id.slice(-4), 16) % 4096;

  function seed(companyId: string): RunRow[] {
    const have = runs.get(companyId);
    if (have) return have;
    const list: RunRow[] = [];
    const now = deps.now().getTime();
    for (const [d, de] of deps.desOf(companyId).entries())
      for (const [i, verdict] of PATTERN.entries()) {
        const n = companyNo(companyId) * 1000 + d * 100 + i + 1;
        const id = uid(0x9c00, n);
        const started = new Date(now - (PATTERN.length - i) * 3_600_000).toISOString();
        const ended = new Date(now - (PATTERN.length - i) * 3_600_000 + 900_000).toISOString();
        const parked = verdict === "not_verified" || verdict === "inconclusive";
        const live = verdict === null || parked;
        list.push({
          company_id: companyId,
          id,
          membership_id: de.membership_id,
          crew_agent_id: null,
          seat_id: de.seat_id,
          agent_profile_id: null,
          owner_membership_id: null,
          identity_mode: "as_agent",
          trigger_kind: i % 2 ? "inbox" : "heartbeat",
          trigger_ref: null,
          wakeup_ids: [],
          state: parked ? "waiting_input" : live ? "running" : "completed",
          state_reason: parked ? `verdict_${verdict}` : null,
          started_at: started,
          ended_at: live ? null : ended,
          parent_run_id: null,
          delegation_depth: 0,
          budget_reserved_micros: 200_000,
          cost_micros: 40_000 + i * 5_000,
          tokens_in: 1200 + i * 100,
          tokens_out: 400 + i * 40,
          model: "claude-sonnet",
          sandbox_kind: null,
          skill_pack_versions: {},
          summary: live ? null : "Weekly pipeline update drafted and checked",
          hand_back: null,
          lease_owner: verdict === null ? "runtime" : null,
          lease_until: verdict === null ? new Date(now + 60_000).toISOString() : null,
          verdict,
          verdict_evidence_ref: verdict
            ? { kind: "object", id: `runs/${companyId}/${id}/verification.json` }
            : null,
          output_ref: verdict ? { kind: "task", id: uid(0x9d00, n) } : null,
          model_path: "gateway",
          model_path_fallback_used: false,
          model_credential_id: null,
          // DC-08: depth-0 runs carry no worker context.
          context_refs: null,
          bundle_sha: null,
          ...FIRST_ATTEMPT,
          // AGENT-STABILITY: the oldest run is the automatic retry of a run its runtime lost.
          ...(i === 0 ? { attempt: 2, retry_of_run_id: uid(0x9c00, n + 500) } : {}),
          task_id: null,
          actor_kind: "digital_employee",
          actor_membership_id: de.membership_id,
          created_at: started,
          updated_at: live ? started : ended,
        });
        logs.set(id, seedLog(companyId, id, de.membership_id, i, started));
      }
    runs.set(companyId, list);
    return list;
  }

  function seedLog(
    companyId: string,
    runId: string,
    by: string,
    i: number,
    at: string,
  ): DecisionRow[] {
    const steps: [string, string, DecisionRow["result"]][] = [
      ["Read the open requisitions", "the weekly update starts from what is open", "ok"],
      ["Draft the update from the ATS export", "the export is the source of record", "ok"],
    ];
    if (i % 3 === 2)
      steps.push(
        ["Fix 1: re-sort the candidate table", "assuming the export is sorted by stage", "failed"],
        ["Fix 2: regroup by stage", "assuming the export is sorted by stage", "failed"],
      );
    return steps.map(([decision, why, result], k) => ({
      company_id: companyId,
      id: uid(0x9e00, Number.parseInt(runId.slice(-6), 16) * 10 + k),
      run_id: runId,
      seq: k + 1,
      decided_at: at,
      decision,
      why,
      evidence_ref:
        k === 1 ? { kind: "object", id: `runs/${companyId}/${runId}/export.csv` } : null,
      result,
      escalation_kind: null,
      actor_kind: "digital_employee",
      actor_membership_id: by,
      created_at: at,
    }));
  }

  const membershipOf = (p: Principal) =>
    p.me.memberships.find((m) => m.company_id === p.company.id)?.membership_id ?? null;

  /** This twin's runs and, after the M2-06 merge, the runs chat's messages point at. */
  const all = (companyId: string): RunRow[] => [
    ...seed(companyId),
    ...(deps.extraRuns?.(companyId) ?? []),
  ];

  /** 05 `run#viewer`: company managers, and the supervisor of the DE's seat. */
  function views(p: Principal, membershipId: string): boolean {
    if (p.manager) return true;
    const de = deps.desOf(p.company.id).find((d) => d.membership_id === membershipId);
    return !!de && de.supervisor.membership_id === membershipOf(p);
  }

  /**
   * The supervisor of a seat (the API's `supervisor#holder` of the run's recorded seat). The fake
   * models seats only through the DEs on them, so a seat is supervised by its DE's supervisor.
   */
  function seatSupervisor(companyId: string, seatId: string): string | null {
    return (
      deps.desOf(companyId).find((d) => d.seat_id === seatId)?.supervisor.membership_id ?? null
    );
  }

  /** 05 `run#viewer` of one run: managers, and the people above the run's own seat. */
  function viewsRun(p: Principal, r: RunRow): boolean {
    if (p.manager) return true;
    return personSeesRun(deps.desOf(p.company.id), membershipOf(p), r);
  }

  /**
   * The supervisor of a run: of its recorded seat; for a run on a seat no fake DE holds (chat's
   * demo runs), its DE's supervisor, as before.
   */
  function runSupervisor(companyId: string, r: RunRow): string | null {
    const bySeat = r.seat_id ? seatSupervisor(companyId, r.seat_id) : null;
    if (bySeat !== null) return bySeat;
    return (
      deps.desOf(companyId).find((d) => d.membership_id === r.membership_id)?.supervisor
        .membership_id ?? null
    );
  }

  function featureOff(): Response {
    return new Response(
      JSON.stringify({
        type: "https://companyos.app/errors/feature_disabled",
        title: "Feature not enabled",
        status: 403,
        code: "feature_disabled",
        feature: "agents",
        request_id: "fake-runs",
        detail: "The agents add-on is not enabled for this company.",
      }),
      { status: 403, headers: { "content-type": "application/problem+json" } },
    );
  }

  /** The run a person may view, or a refusal. */
  function viewable(p: Principal | null, id: string | undefined): RunRow | Response {
    if (!p) return notFound();
    if (!deps.agentsOn(p)) return featureOff();
    const r = all(p.company.id).find((x) => x.id === id);
    return r && viewsRun(p, r) ? r : notFound();
  }

  const handlers: Partial<Record<RouteId, Handler>> = {
    "runs.list": ({ principal: p, url }) => {
      if (!p) return notFound();
      if (!deps.agentsOn(p)) return featureOff();
      const q = url.searchParams;
      const rows = all(p.company.id)
        .filter((r) => viewsRun(p, r))
        .filter((r) => !q.get("state") || r.state === q.get("state"))
        .filter((r) => !q.get("seat_id") || r.seat_id === q.get("seat_id"))
        .filter((r) => !q.get("membership_id") || r.membership_id === q.get("membership_id"))
        .sort((a, b) => (a.id < b.id ? 1 : -1));
      return json(page(rows, url));
    },
    "runs.get": ({ principal: p, params }) => {
      const r = viewable(p, params.id);
      if (r instanceof Response) return r;
      const log = logs.get(r.id) ?? [];
      return json({
        ...r,
        steps: [],
        decisions: log.slice(0, DETAIL_DECISIONS),
        decisions_truncated: log.length > DETAIL_DECISIONS,
      });
    },
    // ACTIVITY: Stop on this twin's live runs too (runs.ts `runs.cancel`: a viewer stops a live
    // run; an ended one is 409 `run_state_conflict`, a stopped one answers as it is).
    "runs.cancel": ({ principal: p, params }) => {
      const r = viewable(p, params.id);
      if (r instanceof Response) return r;
      if (r.state === "cancelled") return json(r);
      if (r.state === "completed" || r.state === "failed")
        return problem(409, "run_state_conflict", "Run already ended", `The run is ${r.state}.`);
      const at = deps.now().toISOString();
      Object.assign(r, {
        state: "cancelled",
        state_reason: "cancelled by a person",
        ended_at: at,
        budget_reserved_micros: 0,
        lease_owner: null,
        lease_until: null,
        updated_at: at,
      });
      return json(r);
    },
    // AGT-UI-2: the supervisor's Resume (verdicts.ts `runs.resume`): a run waiting for review runs
    // again, its verdict cleared into the log. The supervisor of the run's seat or a company owner
    // / admin; another viewer gets 403, a run in any other state 409.
    "runs.resume": ({ principal: p, params, body }) => {
      const r = viewable(p, params.id);
      if (r instanceof Response || !p) return r instanceof Response ? r : notFound();
      // As the API: the supervisor of the run's own seat (not the DE's current one).
      const supervisor = runSupervisor(p.company.id, r);
      const admin = p.roles.some((x) => x === "owner" || x === "admin");
      if (!admin && !(supervisor !== null && supervisor === membershipOf(p)))
        return problem(
          403,
          "forbidden",
          "Forbidden",
          "the run's supervisor or a company owner / admin resumes it",
        );
      if (r.state !== "waiting_input")
        return problem(
          409,
          "run_state_conflict",
          "Run state conflict",
          `only a run waiting for review resumes; this one is ${r.state}`,
        );
      const note = (body as { note?: unknown } | undefined)?.note;
      const at = deps.now().toISOString();
      const log = logs.get(r.id) ?? [];
      if (r.verdict)
        log.push({
          company_id: r.company_id,
          run_id: r.id,
          id: uid(0x9f00, Number.parseInt(r.id.slice(-6), 16) * 10 + log.length),
          seq: log.length + 1,
          decided_at: at,
          decision: "verdict_reset",
          why: `the supervisor cleared the ${r.verdict} verdict${typeof note === "string" && note.trim() ? `: ${note.trim()}` : ""}`,
          evidence_ref: r.verdict_evidence_ref,
          result: "ok",
          escalation_kind: null,
          actor_kind: "person",
          actor_membership_id: membershipOf(p),
          created_at: at,
        });
      logs.set(r.id, log);
      Object.assign(r, {
        state: "running",
        state_reason: null,
        verdict: null,
        verdict_evidence_ref: null,
        output_ref: null,
        updated_at: at,
      });
      return json(r);
    },
    "runs.decisions": ({ principal: p, params, url }) => {
      const r = viewable(p, params.id);
      if (r instanceof Response) return r;
      return json(page(logs.get(r.id) ?? [], url));
    },
    "digitalEmployees.verifiedRate": ({ principal: p, params, url }) => {
      if (!p) return notFound();
      if (!deps.agentsOn(p)) return featureOff();
      const de = deps.desOf(p.company.id).find((d) => d.membership_id === params.id);
      if (!de || !views(p, de.membership_id)) return notFound();
      const months = Math.min(12, Math.max(1, Number(url.searchParams.get("months") ?? 3) || 3));
      const now = deps.now();
      const keys = Array.from({ length: months }, (_, i) =>
        new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - (months - 1 - i), 1))
          .toISOString()
          .slice(0, 7),
      );
      const judged = all(p.company.id).filter(
        (r) => r.membership_id === de.membership_id && r.verdict !== null,
      );
      const periods = keys.map((k) => {
        const inMonth = judged.filter((r) => r.updated_at.slice(0, 7) === k);
        const verified = inMonth.filter((r) => r.verdict === "verified").length;
        const enough = inMonth.length >= MIN_RUNS;
        return {
          period_key: k,
          verified,
          with_verdict: inMonth.length,
          value: enough ? verified / inMonth.length : null,
          status: enough ? ("ok" as const) : ("insufficient_data" as const),
        };
      });
      return json({
        metric_key: "de.verified_rate",
        membership_id: de.membership_id,
        min_runs: MIN_RUNS,
        periods,
      });
    },
  };
  return { handlers, runsOf: seed };
}
